// PROVENANCE: lifted from ptc-tax-lifecycle `web-app/src/components/listFilters/SearchableMultiSelect.tsx` at `7af60ebb`
// (C86), verbatim but for these deviations, each marked `// DEVIATION` where it lands (ptc-docs plan 2026-11-02 wave 4 §1b):
//   1. its five English strings go through i18n (`searchableMultiSelect.*`): this repo's sweep baseline is empty;
//   2. an option may carry a `group` (a heading over its run of options, in the order given), and `dimmed` + `note`
//      (shown dimmed with the note after its label, never hidden or disabled: a use already on another account);
//   3. `hideListActions` drops Select all / Clear, for a single-choice caller (the template's "Add a line").
// Change the original first and copy it here, or record a new deviation here.
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Checkbox, Field, Input, Switch, Text, makeStyles, tokens } from '@fluentui/react-components';
import { Dismiss12Regular } from '@fluentui/react-icons';

// The fleet's ONE lookup-column filter control (fleet filter contract v3 §16.8 / case C86,
// Christian's ruling 2026-11-02): a search box over the options, each with a facet count, the
// selection as removable chips, Select all / Clear, an optional "Exclude these" toggle and an
// optional "(has no value)" row -- never an operator dropdown over a single-value picker.
//
// Deliberately PRESENTATIONAL and independent of the `filter.<col>.<op>` record shape: this file
// knows nothing about filters. ColumnHeaderFilter.tsx is the thin adapter that translates to/from
// that record (via picklistSelection.ts); the Preparer Roster's multi-select preparer picker uses
// this SAME component directly, with neither `noValueOption` nor `excludeToggle`, because a plain
// picker has no "no value"/"exclude" concept -- Christian's own wording was "via the same combobox".
//
// Fluent v9 gotcha (same one columnFilterOps.tsx documents): no bare aria-label on a component that
// embeds further Fluent controls without checking first for a render-loop; this one has none of
// that hazard (plain Input/Checkbox/Switch, no controlled Dropdown), so a normal aria-label is fine.

export interface SearchableMultiSelectOption {
  value: string;
  label: string;
  /** DEVIATION 2: a heading over this option's run (consecutive options with the same group share one). */
  group?: string;
  /** DEVIATION 2: shown dimmed, never hidden or disabled. */
  dimmed?: boolean;
  /** DEVIATION 2: said after the label, e.g. "(now on 4010)". */
  note?: string;
}

export interface SearchableMultiSelectProps {
  /** Accessible label stem, e.g. "Preparer" -- used to build the search box's aria-label. */
  label: string;
  options: SearchableMultiSelectOption[];
  /** Selected option VALUES (never includes a no-value sentinel -- that's `noValueOption` below). */
  selected: string[];
  onChange: (next: string[]) => void;
  /** value -> row count (§16.3). A missing key renders with no count suffix; a present 0 renders
   * dimmed, never hidden or disabled -- it's still reachable from a shared/typed URL. */
  facetCounts?: Record<string, number>;
  /** The "(has no value)" row. Omitted entirely (no row rendered) when the caller has no such
   * concept -- e.g. a plain multi-select picker, not a filter. */
  noValueOption?: { label: string; checked: boolean; count?: number; onChange: (checked: boolean) => void };
  /** The "Exclude these" toggle. Omitted entirely when the caller has no exclude concept. */
  excludeToggle?: { label: string; checked: boolean; onChange: (checked: boolean) => void };
  searchPlaceholder?: string;
  /** Called on Enter while focus is anywhere inside this control. Omitted for a picker with no
   * separate "apply" step (selection commits immediately on every click). */
  onEnter?: () => void;
  /** DEVIATION 3: no Select all / Clear (a single-choice caller). */
  hideListActions?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    width: '100%',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  listActions: {
    display: 'flex',
    gap: '8px',
  },
  listWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '220px',
    overflowY: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: '4px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  noValueRow: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '2px',
    paddingTop: '4px',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    padding: '4px',
  },
  // DEVIATION 2
  group: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    padding: '4px 4px 0',
  },
});

/** `label (count)`, or just `label` when no count is known (§16.3: "not provided" ≠ "computed as zero"). */
function withCount(label: string, count: number | undefined): string {
  return count === undefined ? label : `${label} (${count})`;
}

export function SearchableMultiSelect({
  label,
  options,
  selected,
  onChange,
  facetCounts,
  noValueOption,
  excludeToggle,
  searchPlaceholder,
  onEnter,
  hideListActions,
}: SearchableMultiSelectProps) {
  const styles = useStyles();
  const { t } = useTranslation(); // DEVIATION 1
  const rootRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const visibleOptions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  function toggle(value: string) {
    if (selectedSet.has(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  }

  function selectAllVisible() {
    const merged = new Set(selected);
    for (const o of visibleOptions) merged.add(o.value);
    onChange(Array.from(merged));
  }

  function clearList() {
    onChange([]);
    noValueOption?.onChange(false);
  }

  function moveFocus(delta: 1 | -1) {
    const root = rootRef.current;
    if (!root) return;
    const focusables = Array.from(root.querySelectorAll<HTMLElement>('input[type="checkbox"]'));
    if (focusables.length === 0) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? focusables.indexOf(active) : -1;
    if (idx === -1) {
      focusables[delta === 1 ? 0 : focusables.length - 1]?.focus();
      return;
    }
    const next = Math.min(Math.max(idx + delta, 0), focusables.length - 1);
    focusables[next]?.focus();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    } else if (e.key === 'Enter' && onEnter) {
      e.preventDefault();
      onEnter();
    }
    // Escape is deliberately NOT handled here -- it bubbles to the panel's own dismiss().
  }

  const chips: { key: string; text: string; onRemove: () => void }[] = [
    ...selected.map((v) => ({
      key: v,
      text: options.find((o) => o.value === v)?.label ?? v,
      onRemove: () => toggle(v),
    })),
    ...(noValueOption?.checked ? [{ key: '__novalue__', text: noValueOption.label, onRemove: () => noValueOption.onChange(false) }] : []),
  ];

  return (
    <div
      ref={rootRef}
      className={styles.root}
      role="group"
      aria-label={t('searchableMultiSelect.group', { label })} // DEVIATION 1
      onKeyDown={handleKeyDown}
    >
      {excludeToggle ? (
        <Switch
          label={excludeToggle.label}
          checked={excludeToggle.checked}
          onChange={(_, d) => excludeToggle.onChange(d.checked)}
        />
      ) : null}

      {chips.length > 0 ? (
        <div className={styles.chips} data-testid="searchable-multiselect-chips">
          {chips.map((chip) => (
            <Badge
              key={chip.key}
              shape="rounded"
              appearance="tint"
              icon={<Dismiss12Regular onClick={chip.onRemove} style={{ cursor: 'pointer' }} />}
              iconPosition="after"
            >
              {chip.text}
            </Badge>
          ))}
        </div>
      ) : null}

      <Field label={{ children: t('searchableMultiSelect.search', { label }), className: 'sr-only' }}>
        <Input
          size="small"
          value={search}
          placeholder={searchPlaceholder ?? t('searchableMultiSelect.placeholder')}
          onChange={(_, d) => setSearch(d.value)}
        />
      </Field>

      {hideListActions ? null : (
        <div className={styles.listActions}>
          <Button size="small" appearance="subtle" onClick={selectAllVisible} disabled={visibleOptions.length === 0}>
            {t('searchableMultiSelect.selectAll')}
          </Button>
          <Button size="small" appearance="subtle" onClick={clearList} disabled={selected.length === 0 && !noValueOption?.checked}>
            {t('searchableMultiSelect.clear')}
          </Button>
        </div>
      )}

      <div className={styles.listWrap}>
        {visibleOptions.length === 0 ? (
          <div className={styles.empty}>{t('searchableMultiSelect.noMatches')}</div>
        ) : (
          visibleOptions.map((o, i) => {
            const count = facetCounts?.[o.value];
            // DEVIATION 2: a heading where the group changes; dimmed + note, never hidden.
            const heading = o.group && o.group !== visibleOptions[i - 1]?.group ? o.group : null;
            const text = withCount(o.label, count);
            return (
              <div key={o.value}>
                {heading ? <Text as="p" className={styles.group}>{heading}</Text> : null}
                <div className={styles.option} style={count === 0 || o.dimmed ? { opacity: 0.5 } : undefined}>
                  <Checkbox
                    checked={selectedSet.has(o.value)}
                    onChange={() => toggle(o.value)}
                    label={o.note ? `${text} ${o.note}` : text}
                  />
                </div>
              </div>
            );
          })
        )}
        {noValueOption ? (
          <div className={styles.noValueRow} style={noValueOption.count === 0 ? { opacity: 0.5 } : undefined}>
            <Checkbox
              checked={noValueOption.checked}
              onChange={(_, d) => noValueOption.onChange(!!d.checked)}
              label={withCount(noValueOption.label, noValueOption.count)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SearchableMultiSelect;
